import {
    AccountInfo,
    CacheLookupPolicy,
    InteractionRequiredAuthError,
    IPublicClientApplication,
    NavigationClient,
    NavigationOptions,
    PublicClientApplication,
} from "@azure/msal-browser";
import jwt from "jsonwebtoken";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

export const msalConfig = {
    auth: {
        clientId: "bd3776bf-aee2-47b3-975f-87fd8667c3f2",
        authority: "https://login.microsoftonline.com/cc8d834f-85f9-44cc-88e6-3e5b8b390dea/v2.0",
        redirectUri: "/",
        postLogoutRedirectUri: "/",
    },
    system: {
        allowPlatformBroker: false, // Disables WAM Broker
        navigateToLoginRequestUrl: true, // Set false to prevent automatic navigation back to the original request URL
    },
    cache: {
        // Safari requires sessionStorage due to ITP; other browsers use localStorage
        cacheLocation:
            typeof navigator !== "undefined" && /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
                ? "sessionStorage"
                : "localStorage",
    },
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const loginRequest = {
    scopes: ["bd3776bf-aee2-47b3-975f-87fd8667c3f2/.default"],
};

export class CustomNavigationClient extends NavigationClient {
    private router: AppRouterInstance;

    constructor(router: AppRouterInstance) {
        super();
        this.router = router;
    }

    /**
     * Navigates to other pages within the same web application
     * You can use the useRouter hook provided by next.js to take advantage of client-side routing
     * @param url
     * @param options
     */
    async navigateInternal(url: string, options: NavigationOptions) {
        const relativePath = url.replace(window.location.origin, "");
        if (options.noHistory) {
            this.router.replace(relativePath);
        } else {
            this.router.push(relativePath);
        }

        return false;
    }
}

const buildTokenRequest = (instance: IPublicClientApplication, accounts: AccountInfo[]) => {
    const account = instance.getActiveAccount() ?? accounts.at(0);
    if (!account) {
        throw new Error("No account available for token request");
    }
    return { ...loginRequest, account };
};

/**
 * True when a failed token acquisition means the user has to sign in again,
 * as opposed to a transient network or iframe problem.
 */
export const isInteractionRequired = (error: unknown): boolean => error instanceof InteractionRequiredAuthError;

/**
 * Acquires an access token. Never navigates: callers get a rejected promise.
 *
 * `acquireTokenRedirect` is a full top-level navigation that destroys all
 * in-memory React state — including a half-typed prompt — and this function is
 * called from ~28 places, most of them background effects and fire-and-forget
 * writes. Recovering an expired session is `getJWTInteractive`'s job instead.
 *
 * `CacheLookupPolicy.AccessTokenAndRefreshToken` is deliberate: it is absent
 * from MSAL's `iFrameRenewalPolicies`, so no hidden iframe is opened and
 * `monitor_window_timeout` — a 10s stall that the VPN this app requires
 * triggers routinely — becomes structurally impossible here. An unusable
 * refresh token fails fast with `InteractionRequiredAuthError` instead.
 */
export const getJWT = async (instance: IPublicClientApplication, accounts: AccountInfo[]) => {
    const response = await instance.acquireTokenSilent({
        ...buildTokenRequest(instance, accounts),
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
    });
    return response.accessToken;
};

/**
 * Acquires an access token, falling back to a full-page redirect to the identity
 * provider when the session can only be renewed interactively.
 *
 * Because that redirect discards everything in memory, this belongs only where
 * there is nothing to lose: the boot gate on `/`, and the explicit "Sign in
 * again" action on an expired-session banner.
 */
export const getJWTInteractive = async (instance: IPublicClientApplication, accounts: AccountInfo[]) => {
    const tokenRequest = buildTokenRequest(instance, accounts);
    try {
        const response = await instance.acquireTokenSilent(tokenRequest);
        return response.accessToken;
    } catch (error) {
        if (isInteractionRequired(error)) {
            await instance.acquireTokenRedirect(tokenRequest);
        }
        throw error;
    }
};

export const decodeJWT = (token: string) => {
    return jwt.decode(token);
};

export const isJwtExpired = (token: string): boolean => {
    const decoded = decodeJWT(token);
    if (decoded && typeof decoded === "object" && "exp" in decoded) {
        const exp = (decoded.exp ?? 0) * 1000;
        const now = Date.now();
        return now > exp;
    }
    return true;
};
